import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Briefcase, Factory, Users, Handshake, Calendar, Crown, Languages,
  Building2, Cpu, Ship, Sparkles, MapPin, Mail, Phone, ArrowRight,
  CheckCircle2, Loader2, Sparkles as SparklesIcon, Globe, Award, ShieldCheck,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import SectionReveal from "@/components/SectionReveal";
import SEO, { SITE } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "971589656886";

const BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Business Travel & B2B China Tours",
  provider: {
    "@type": "TravelAgency",
    name: "The China Explorer",
    url: SITE.url,
    telephone: "+971589656886",
    email: "hello@thechinaexplorer.com",
    address: { "@type": "PostalAddress", addressLocality: "Dubai", addressCountry: "AE" },
  },
  areaServed: { "@type": "Country", name: "China" },
  description:
    "Bespoke business travel services to China — factory visits, supplier matching, trade fair coverage, executive travel and local interpretation.",
  offers: { "@type": "Offer", availability: "https://schema.org/InStock" },
};

const AUDIENCES = [
  { icon: Briefcase, label: "Entrepreneurs" },
  { icon: Award, label: "Investors" },
  { icon: Users, label: "Corporate Teams" },
  { icon: Factory, label: "Procurement Professionals" },
  { icon: Crown, label: "Family Offices" },
  { icon: Building2, label: "Educational Institutions" },
  { icon: Handshake, label: "Trade Delegations" },
  { icon: Globe, label: "Government & Business Missions" },
];

const SERVICES = [
  { icon: Factory, title: "Factory Visits", body: "Arrange visits to manufacturing facilities across China — Pearl River, Yangtze Delta, Bohai Rim." },
  { icon: Handshake, title: "Supplier Matching", body: "Connect businesses with verified suppliers and manufacturers, pre-vetted and reference-checked." },
  { icon: Users, title: "Business Meetings", body: "Coordinate meetings with potential partners, distributors and OEMs across industries." },
  { icon: Calendar, title: "Trade Fair Support", body: "Full coordination for Canton Fair, CIIE, Yiwu Expo and 50+ specialized exhibitions." },
  { icon: Crown, title: "Executive Travel Planning", body: "Premium business travel with private cars, 5-star hotels, lounge access and dedicated trip managers." },
  { icon: Languages, title: "Local Interpretation", body: "Bilingual interpreters (English / Arabic / Mandarin) and on-ground coordination throughout your trip." },
];

const CITIES = [
  { name: "Shanghai", sub: "Financial & innovation hub", img: "/img/destinations/shanghai.png" },
  { name: "Shenzhen", sub: "Technology & manufacturing capital", img: "/img/cuisine/peking_duck.png" },
  { name: "Guangzhou", sub: "Trade & export gateway", img: "/img/cuisine/hunan_pork.png" },
  { name: "Hangzhou", sub: "Innovation & e-commerce ecosystem", img: "/img/destinations/zhangjiajie.png" },
  { name: "Beijing", sub: "Government, business & cultural center", img: "/img/destinations/xian.png" },
  { name: "Yiwu", sub: "Global sourcing & wholesale marketplace", img: "/img/cuisine/xinjiang_lamb.png" },
];

const JOURNEY = [
  { d: "Day 1", t: "Arrival & Business Briefing", b: "Airport VIP pickup, hotel check-in, market briefing with our China head." },
  { d: "Day 2", t: "Factory Visits", b: "Two pre-vetted manufacturing sites; private interpreter throughout." },
  { d: "Day 3", t: "Supplier Meetings", b: "Back-to-back meetings at our office or supplier locations — negotiation support included." },
  { d: "Day 4", t: "Trade Fair / Industry Visits", b: "Coverage of the relevant exhibition + curated industry park tours." },
  { d: "Day 5", t: "Networking & Departure", b: "B2B networking dinner, contract review, departure transfer." },
];

const WHY = [
  { icon: Globe, t: "Dubai-Based International Perspective" },
  { icon: MapPin, t: "Deep China Market Understanding" },
  { icon: Languages, t: "English-Speaking Coordination" },
  { icon: Crown, t: "Hospitality & Client Service Expertise" },
  { icon: Sparkles, t: "Fully Customized Planning" },
  { icon: ShieldCheck, t: "Trusted Local Network" },
  { icon: Users, t: "Personalized 24/7 Support" },
];

const ChinaBusinessExplorer = () => {
  const [form, setForm] = useState({
    name: "", company: "", country: "", industry: "", travelers: 1,
    travel_dates: "", cities: "", objectives: "", budget: "", email: "", phone: "",
  });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    const enriched = [
      `BUSINESS INQUIRY — China Business Explorer`, "",
      `Name: ${form.name}`, `Company: ${form.company}`, `Country: ${form.country}`,
      `Industry: ${form.industry}`, `Travelers: ${form.travelers}`,
      `Travel dates: ${form.travel_dates}`, `Preferred cities: ${form.cities}`,
      `Business objectives: ${form.objectives}`, `Budget: ${form.budget}`,
      `WhatsApp: ${form.phone}`,
    ].join("\n");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.email,
        subject: `Business Travel Inquiry — ${form.company || form.country}`,
        message: enriched,
      });
      setStatus("success");
      setForm({ name: "", company: "", country: "", industry: "", travelers: 1, travel_dates: "", cities: "", objectives: "", budget: "", email: "", phone: "" });
    } catch (err) { console.error(err); setStatus("error"); }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="App min-h-screen bg-[#F7F5F0] text-[#121212] font-body antialiased">
      <SEO
        title="China Business Explorer — Factory Visits, Supplier Sourcing & B2B Travel"
        description="Executive business travel to China for companies, investors and procurement teams — factory visits, supplier matching, Canton Fair coverage and interpreter services."
        path="/china-business-explorer"
        image="/img/destinations/shanghai_business.png"
        keywords={[
          "business travel china",
          "factory visits china",
          "supplier sourcing china",
          "canton fair packages",
          "china b2b travel",
          "executive china tours",
          "trade fair china",
          "manufacturing tours china",
        ]}
        jsonLd={BUSINESS_JSONLD}
      />
      <Header />

      {/* HERO */}
      <section className="relative min-h-[680px] h-[90vh] w-full overflow-hidden bg-black flex items-end">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/img/destinations/shanghai_business.png)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 w-full">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-6">✦ For Business Travelers</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-[5.5rem] leading-[1.05] tracking-tight text-white max-w-4xl">
            China Business Explorer
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
            Helping international companies, investors, entrepreneurs and organizations explore business opportunities in China — through factory visits, supplier sourcing, trade fairs, executive travel and local expertise.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href={`#business-inquiry`} className="group bg-[#C62828] hover:bg-[#B71C1C] text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-3 transition-all">
              Request Consultation <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi! I'd like to plan a business trip to China.")}`}
               target="_blank" rel="noopener noreferrer"
               className="border border-white/40 text-white hover:bg-white hover:text-[#121212] px-8 py-4 uppercase tracking-wider text-sm font-semibold transition-all">
              Plan My Business Trip
            </a>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="bg-[#F7F5F0] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionReveal className="max-w-3xl">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">✦ Who This Is For</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Built for serious China-bound professionals.</h2>
          </SectionReveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {AUDIENCES.map((a, i) => { const Icon = a.icon; return (
              <SectionReveal key={a.label} delay={i*0.05} className="bg-white border border-[#EAE6DF] p-6 hover:border-[#C62828]/30 hover:-translate-y-0.5 transition-all">
                <Icon size={22} className="text-[#C62828]" strokeWidth={1.5} />
                <p className="mt-4 font-heading text-base text-[#121212]">{a.label}</p>
              </SectionReveal>
            ); })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#EAE6DF] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionReveal className="max-w-3xl">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#0E2A5C] font-semibold mb-4">✦ Our Services</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Six pillars of business travel done right.</h2>
          </SectionReveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => { const Icon = s.icon; return (
              <SectionReveal key={s.title} delay={i*0.06} className="group bg-white border border-black/5 p-7 hover:border-[#0E2A5C]/30 hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-[#0E2A5C]/10 text-[#0E2A5C] group-hover:bg-[#0E2A5C] group-hover:text-white transition-colors">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-heading text-2xl text-[#121212]">{s.title}</h3>
                <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{s.body}</p>
              </SectionReveal>
            ); })}
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="bg-[#F7F5F0] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionReveal className="max-w-3xl">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">✦ Featured Business Destinations</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Six cities, every China opportunity.</h2>
          </SectionReveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CITIES.map((c, i) => (
              <SectionReveal key={c.name} delay={i*0.06} className="relative aspect-[4/3] overflow-hidden border border-[#EAE6DF] group">
                <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <h3 className="font-heading text-2xl">{c.name}</h3>
                  <p className="text-sm text-white/80 mt-1">{c.sub}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE JOURNEY */}
      <section className="bg-[#0E2A5C] text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionReveal className="max-w-3xl">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-4">✦ Sample Business Journey</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">A typical 5-day business mission.</h2>
            <p className="mt-6 text-white/75 text-lg leading-relaxed">Each itinerary is customized — this is a representative outline of a successful B2B trip we organize regularly.</p>
          </SectionReveal>
          <div className="mt-12 grid md:grid-cols-5 gap-4">
            {JOURNEY.map((j, i) => (
              <SectionReveal key={j.d} delay={i*0.08} className="bg-white/5 border border-white/10 p-6 md:p-7">
                <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold">{j.d}</p>
                <p className="mt-3 font-heading text-xl leading-tight">{j.t}</p>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">{j.b}</p>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY THE CHINA EXPLORER */}
      <section className="bg-[#EAE6DF] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionReveal className="max-w-3xl">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">✦ Why The China Explorer</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Dubai-based. China-rooted. Built for business.</h2>
          </SectionReveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w, i) => { const Icon = w.icon; return (
              <SectionReveal key={w.t} delay={i*0.05} className="bg-white border border-black/5 p-6 flex items-start gap-4">
                <span className="w-11 h-11 flex items-center justify-center bg-[#C62828]/10 text-[#C62828] flex-shrink-0"><Icon size={20} strokeWidth={1.5}/></span>
                <p className="font-heading text-base text-[#121212]">{w.t}</p>
              </SectionReveal>
            ); })}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="business-inquiry" className="bg-[#F7F5F0] py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <SectionReveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">✦ Business Inquiry</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Tell us about your business trip.</h2>
            <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">We respond within 24 hours with a tailored proposal.</p>
          </SectionReveal>

          <SectionReveal delay={0.15} className="mt-12">
            <form onSubmit={submit} data-testid="business-form" className="bg-white border border-black/5 p-8 md:p-12 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Inp label="Name" v={form.name} on={(v)=>upd("name",v)} required testid="biz-name"/>
                <Inp label="Company name" v={form.company} on={(v)=>upd("company",v)} required testid="biz-company"/>
                <Inp label="Country" v={form.country} on={(v)=>upd("country",v)} required testid="biz-country"/>
                <Inp label="Industry" v={form.industry} on={(v)=>upd("industry",v)} placeholder="e.g. Electronics, Apparel, F&B" testid="biz-industry"/>
                <Inp label="Number of travelers" type="number" min="1" v={form.travelers} on={(v)=>upd("travelers",v)} testid="biz-travelers"/>
                <Inp label="Travel dates" v={form.travel_dates} on={(v)=>upd("travel_dates",v)} placeholder="e.g. 12-22 Apr 2026" testid="biz-dates"/>
                <Inp label="Preferred cities" v={form.cities} on={(v)=>upd("cities",v)} placeholder="e.g. Shenzhen, Yiwu, Guangzhou" testid="biz-cities"/>
                <Sel label="Budget range" v={form.budget} on={(v)=>upd("budget",v)} testid="biz-budget" opts={["", "Under $5,000", "$5,000 – $15,000", "$15,000 – $30,000", "$30,000 – $75,000", "$75,000+"]}/>
                <Inp label="Email" type="email" v={form.email} on={(v)=>upd("email",v)} required testid="biz-email"/>
                <Inp label="WhatsApp" type="tel" v={form.phone} on={(v)=>upd("phone",v)} placeholder="+..." testid="biz-phone"/>
              </div>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">Business objectives</span>
                <textarea data-testid="biz-objectives" rows={4} value={form.objectives} onChange={(e)=>upd("objectives",e.target.value)}
                  placeholder="Sourcing 3 categories, visiting 5 factories, meeting potential distributors, attending Canton Fair Phase 2..."
                  className="w-full bg-[#F7F5F0] border border-black/12 px-4 py-3 text-[#121212] outline-none focus:border-[#C62828] resize-none"/>
              </label>
              <button data-testid="biz-submit" type="submit" disabled={status==="loading"}
                className="bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-2 transition-colors">
                {status==="loading" ? <><Loader2 size={16} className="animate-spin"/> Sending...</> : <>Send Business Inquiry <ArrowRight size={14}/></>}
              </button>
              {status==="success" && <p data-testid="biz-success" className="inline-flex items-center gap-2 text-[#2E5D4B] text-sm ml-4"><CheckCircle2 size={16}/> Thank you. Our team will respond within 24 hours.</p>}
              {status==="error" && <p data-testid="biz-error" className="text-[#C62828] text-sm ml-4">Something went wrong. Please try again or WhatsApp us.</p>}
            </form>
          </SectionReveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0A0A0A] text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-4">✦ Get Started</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Ready to explore opportunities in China?</h2>
          <p className="mt-6 text-lg text-white/75 max-w-2xl mx-auto">Let us help you create a productive and seamless business journey to China.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href="#business-inquiry" className="group bg-[#D4AF37] hover:bg-[#bf9a2b] text-[#0A0A0A] px-8 py-4 uppercase tracking-wider text-sm font-bold inline-flex items-center gap-2">
              Request Proposal <ArrowRight size={14}/>
            </a>
            <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi! I'd like to schedule a consultation about a China business trip.")}`}
               target="_blank" rel="noopener noreferrer"
               className="border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white px-8 py-4 uppercase tracking-wider text-sm font-bold inline-flex items-center gap-2">
              Schedule Consultation
            </a>
          </div>
          <Link to="/" className="mt-10 inline-block text-xs uppercase tracking-[0.2em] text-white/55 hover:text-white">← Back to main site</Link>
        </div>
      </section>

      <Footer />
      <AIChat />
    </div>
  );
};

const Inp = ({ label, v, on, type="text", required, placeholder, min, testid }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">{label}{required && " *"}</span>
    <input data-testid={testid} type={type} required={required} min={min} placeholder={placeholder}
      value={v} onChange={(e)=>on(e.target.value)}
      className="w-full bg-[#F7F5F0] border border-black/12 px-4 py-3 text-[#121212] outline-none focus:border-[#C62828]"/>
  </label>
);

const Sel = ({ label, v, on, opts, testid }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">{label}</span>
    <select data-testid={testid} value={v} onChange={(e)=>on(e.target.value)}
      className="w-full bg-[#F7F5F0] border border-black/12 px-4 py-3 text-[#121212] outline-none focus:border-[#C62828]">
      {opts.map(o => <option key={o} value={o}>{o || "Select..."}</option>)}
    </select>
  </label>
);

export default ChinaBusinessExplorer;
